// Assistant events go to the GA4 tag in the application layout.
// Parameters are allowlisted so message text, addresses, and names never leave the page.

const EVENTS = new Set([
  "assistant_opened",
  "conversation_started",
  "assistant_message_sent",
  "draft_approved",
  "goal_approved",
  "autonomous_replies_enabled",
  "autonomous_replies_disabled",
  "silence_follow_up_enabled",
  "silence_follow_up_disabled",
  "authorization_updated",
  "google_permission_requested",
  "google_permission_granted",
  "conversation_deleted",
  "standing_rules_saved",
  "manual_message_added",
  "manual_draft_copied",
  "manual_draft_shared",
]);

const MODES = new Set(["new_email", "reply_email", "reply_chat", "manual"]);
const FEATURES = new Set(["gmail", "chat"]);
const INTENTS = new Set(["message", "set_goal"]);
const ACTION_TYPE = /^(draft|send|auto)_[a-z0-9_]{1,40}$/;

const BOOLEAN_PARAMS = ["gmail_connected", "gmail_authorized", "chat_authorized", "require_approval", "has_expiry", "silence_follow_up"];
const SILENCE_WAITS = new Set([4, 24, 48, 72, 168]);
const SILENCE_MAXES = new Set([1, 2, 3]);

export function assistantMessageIntent(text, goalPrompt) {
  const normalized = (text ?? "").toString().trim();
  if (goalPrompt && normalized === goalPrompt) return "set_goal";
  return "message";
}

export function authorizationAnalyticsParams(mode, settings = {}) {
  const maxSends = Number(settings.maxSends);
  const silenceAfterHours = Number(settings.silenceFollowUpAfterHours);
  const silenceMax = Number(settings.silenceFollowUpMax);
  const params = {
    mode,
    require_approval: Boolean(settings.requireApproval),
    has_expiry: Boolean(settings.expiresInHours),
    max_sends: Number.isFinite(maxSends) && maxSends > 0 ? maxSends : 0,
    silence_follow_up: Boolean(settings.silenceFollowUp),
  };
  if (SILENCE_WAITS.has(silenceAfterHours)) params.silence_after_hours = silenceAfterHours;
  if (SILENCE_MAXES.has(silenceMax)) params.silence_max = silenceMax;
  return params;
}

function sanitizeParams(params) {
  const safe = {};
  if (!params || typeof params !== "object") return safe;

  if (MODES.has(params.mode)) safe.mode = params.mode;
  if (FEATURES.has(params.feature)) safe.feature = params.feature;
  if (INTENTS.has(params.intent)) safe.intent = params.intent;
  if (typeof params.action_type === "string" && ACTION_TYPE.test(params.action_type)) {
    safe.action_type = params.action_type;
  }

  BOOLEAN_PARAMS.forEach((key) => {
    if (typeof params[key] === "boolean") safe[key] = params[key];
  });

  const maxSends = Number(params.max_sends);
  if (Number.isFinite(maxSends) && maxSends >= 0 && maxSends <= 1000) safe.max_sends = maxSends;

  const silenceAfterHours = Number(params.silence_after_hours);
  if (SILENCE_WAITS.has(silenceAfterHours)) safe.silence_after_hours = silenceAfterHours;

  const silenceMax = Number(params.silence_max);
  if (SILENCE_MAXES.has(silenceMax)) safe.silence_max = silenceMax;

  return safe;
}

export function trackAssistantEvent(name, params = {}, { beacon = false } = {}) {
  if (!EVENTS.has(name)) return;

  const gtag = typeof window !== "undefined" ? window.gtag : undefined;
  if (typeof gtag !== "function") return;

  const eventParams = sanitizeParams(params);
  if (beacon) eventParams.transport_type = "beacon";

  try {
    gtag("event", name, eventParams);
  } catch (_error) {
    // A blocked or broken analytics tag should not interrupt the assistant.
  }
}
